/**
 * `Docs_Executor.ts` — port of Unity's `AI/Executor/PDF_Executor.cs`
 * (Knowledge tool family). See `Docs_Registry.ts` for the file-name note.
 *
 * Each tool publishes what it searched and the SOURCE it used
 * (`"string search"` / `"semantic search"` / `"llm"`) to the conversation
 * presentation, then attaches any citations, exactly as Unity does — that is
 * what makes an answer traceable back to a document.
 *
 * `Save_PDF_placement` has no web counterpart (no in-app PDF panel) and is
 * not registered, so it never reaches this dispatcher.
 *
 * The last three cases are Spark Preview stubs — everything the spec wants to
 * happen INSIDE an open document. The web opens documents in a browser tab, so
 * there is nothing to highlight in, step through, or search. See
 * `previewNotImplemented.ts`.
 */

import { AgentToolNames } from "@ai/registry/agentToolNames";
import type { DocsQuery, IAIContextEngineModel } from "@ai/context/AIContextEngineModel.Core";
import {
  K_EMPTY_PART_HIERARCHY_CONTEXT,
  formatDocumentPassages,
  formatNameSearchGroups,
  isNotFoundAnswer,
  passagesForNameSearch,
  type AIFileCitation,
  type KnowledgeContextResponse,
  type NameSearch,
  type NameSearchGroup,
  type NameSearches,
  type RetrievedPassage,
} from "@ai/context/AIContextEngineModel.KnowledgeTransport";
import type { AICitationCandidate } from "@ai/interface/AIConversationPresentationModel";
import { previewNotImplemented } from "./previewNotImplemented";
import { toChipCitations, withDocumentDisplayNames } from "./citationDisplayNames";
import { agentFacingDocumentName } from "@models/docs/documentDisplayNames";
import {
  getString,
  type AgentToolExecutorHost,
  type IDomainExecutor,
  type ToolArgs,
} from "./AgentToolExecutorModel";

/** `Prompt_Search_Docs`' internal argument for a part's already-built searches. */
export const K_PART_SEARCHES_ARG = "partSearches";

export class DocsExecutor implements IDomainExecutor {
  constructor(
    private readonly m_contextEngine: IAIContextEngineModel,
    private readonly m_host: AgentToolExecutorHost,
  ) {}

  async tryExecute(toolName: string, args: ToolArgs): Promise<string | null> {
    switch (toolName) {
      case AgentToolNames.PromptSearchDocs:
        return this.promptSearchDocsAsync(args);
      case AgentToolNames.SearchDocByFileName:
        return this.searchDocByFileNameAsync(args);
      case AgentToolNames.FindAndShowPDF:
        return this.findAndShowPdfAsync(args);
      case AgentToolNames.OpenDocAtReference:
        return previewNotImplemented(
          "Opening a document at the exact passage, highlighted",
          `opening the whole document with ${AgentToolNames.FindAndShowPDF}, and reading the passage out`,
        );
      case AgentToolNames.GoToNextDocResult:
        return previewNotImplemented("Stepping through matches inside an open document");
      case AgentToolNames.SearchInOpenDoc:
        return previewNotImplemented(
          "Searching inside the document the user has open",
          `searching every project document with ${AgentToolNames.PromptSearchDocs}`,
        );
      default:
        return null;
    }
  }

  /** Unity `PromptSearchDocsAsync` (`PDF_Executor.cs:45-70`). */
  private async promptSearchDocsAsync(args: ToolArgs): Promise<string> {
    const prompt = getString(args, "prompt");
    if (!prompt) return "error: prompt_required";

    // Unity appends `BuildDocsPrompt`'s "| Docs: search project documents…"
    // instruction here; the web sends the vector store no instruction text
    // (see `buildNameSearches`). It does search by each name of any aliased
    // part the question names, since the agent often searches with the user's
    // word alone (see `buildDocsQuery`). `Get_Part_Context` passes its own part's
    // searches, and no part lines because its result carries that part's line.
    const partSearches = readPartSearches(args);
    const direct = partSearches ? null : this.m_contextEngine.buildDocsQuery(prompt);
    const { searches, skippedNames } = partSearches ?? (direct as DocsQuery);
    const namedPartLines = direct?.namedPartLines ?? [];
    const query = searches.map((search) => search.query).join(" | ");
    const presentation = this.m_host.conversationPresentation;
    presentation?.publishToolUsagePrompt(AgentToolNames.PromptSearchDocs, query);
    presentation?.publishToolUsageSource(AgentToolNames.PromptSearchDocs, "semantic search");

    // Every search finishes before the agent sees anything, so it chooses from
    // all of them. One that fails does not take the others with it.
    const settled = await Promise.allSettled(
      searches.map((search) =>
        this.m_contextEngine.queryKnowledgeContextAsync(
          search.query,
          K_EMPTY_PART_HIERARCHY_CONTEXT,
          // `semanticOnly` — this tool exists for CONTENT questions, so the cheap
          // manifest pre-pass that answers "which file is it" is skipped.
          true,
        ),
      ),
    );
    const responses = settled.map((outcome) => {
      if (outcome.status === "rejected") return null;
      this.m_host.trackHelperAgentUsage?.("Knowledge Context Agent", outcome.value?.usage ?? null);
      return outcome.value ?? null;
    });

    if (searches.length > 1) {
      const grouped = await this.groupNameSearchesAsync(searches, responses, skippedNames, query);
      if (grouped !== null) return withNamedParts(grouped, namedPartLines);
    }
    return this.finishSearchAsync(
      responses.find((candidate) => candidate !== null) ?? null,
      query,
      namedPartLines,
    );
  }

  /**
   * One tool result from all of a question's searches, or null when none of them
   * returned a passage, so the single-search path below answers exactly as it
   * always has (the not-found text, and the `file_search` fallback's references).
   *
   * The candidates are published as ONE batch: `recentSearchPassages` reaches
   * back over the last 4 batches, and that window has to count questions, or one
   * question about a part with several aliases would push out the search before it.
   */
  private async groupNameSearchesAsync(
    searches: ReadonlyArray<NameSearch>,
    responses: ReadonlyArray<KnowledgeContextResponse | null>,
    skippedNames: ReadonlyArray<string>,
    query: string,
  ): Promise<string | null> {
    const label = (search: NameSearch) => search.name ?? search.query;
    const failedNames = searches
      .filter((_, index) => !answeredBySearch(responses[index]))
      .map(label);
    const labelled = await Promise.all(
      responses.map((response) =>
        response?.passages ? withPassageDisplayNames(this.m_host, response.passages) : [],
      ),
    );

    // A passage two names found is shown once, under the first, saying so.
    const passageKey = (passage: AICitationCandidate) => `${passage.fileId}\n${passage.text}`;
    const namesByPassage = new Map<string, string[]>();
    labelled.forEach((candidates, index) => {
      for (const candidate of candidates) {
        const key = passageKey(candidate);
        namesByPassage.set(key, [...(namesByPassage.get(key) ?? []), label(searches[index])]);
      }
    });

    const shown = new Set<string>();
    const candidates: AICitationCandidate[] = [];
    const groups: NameSearchGroup[] = [];
    const emptyNames: string[] = [];
    labelled.forEach((found, index) => {
      const name = label(searches[index]);
      const fresh = found
        .filter((candidate) => !shown.has(passageKey(candidate)))
        .slice(0, passagesForNameSearch(index, searches.length));
      for (const candidate of fresh) shown.add(passageKey(candidate));
      if (fresh.length > 0) {
        candidates.push(...fresh);
        groups.push({
          name,
          passages: fresh.map((candidate) => ({
            fileName: candidate.fileName,
            title: candidate.displayName,
            score: candidate.score,
            text: candidate.text,
            alsoFoundAs: (namesByPassage.get(passageKey(candidate)) ?? []).filter((other) => other !== name),
          })),
        });
      } else if (found.length === 0 && !failedNames.includes(name)) {
        emptyNames.push(name);
      }
    });
    if (groups.length === 0) {
      // Nothing found while a search failed is not "the documents do not say".
      return responses.some(isFailedSearch)
        ? `error: document_search_failed. Nothing is known under: ${failedNames.join(", ")}`
        : null;
    }

    this.m_host.conversationPresentation?.publishCitationCandidates(query, candidates);
    return formatNameSearchGroups(groups, emptyNames, failedNames, skippedNames);
  }

  /** What one search returned, as the tool result. */
  private async finishSearchAsync(
    response: KnowledgeContextResponse | null,
    query: string,
    namedPartLines: ReadonlyArray<string>,
  ): Promise<string> {
    const presentation = this.m_host.conversationPresentation;

    // THE DIRECT SEARCH PUBLISHES NO CHIPS. Its passages are CANDIDATE
    // references, held until Spark's answer is known and then judged by the
    // attribution check, which keeps only the documents that state what Spark
    // said (`AIAnswerAttribution.ts`, Spark Answer References Phase 1). What
    // used to be shown here was every document scoring 0.6 or more, which is
    // similarity: in the 2026-10-06 broaching trace, 15 of the 24 chips under 7
    // answers pointed at a document that stated nothing in the answer.
    //
    // The agent still reads every passage, each with its document's title, so
    // it can name a document if asked. The `Sources (N)` line is gone from this
    // path: it listed that same similarity set, and nothing the agent does
    // depends on it (`answer.md`'s `Ranked document list` says the line MAY be
    // there, which stays true for Unity).
    if (response?.passages) {
      const candidates = await withPassageDisplayNames(this.m_host, response.passages);
      presentation?.publishCitationCandidates(query, candidates);
      return withNamedParts(
        formatDocumentPassages(
          candidates.map((candidate) => ({
            fileName: candidate.fileName,
            title: candidate.displayName,
            score: candidate.score,
            text: candidate.text,
          })),
        ),
        namedPartLines,
      );
    }

    // The compatibility `file_search` fallback (and a search that found nothing,
    // which has no passages either) keeps the old reference list: there the
    // helper model wrote the answer and annotated what it used.
    //
    // `sources`, not `citations`: the sub-agent annotates the document it quoted
    // and routinely leaves the others unmarked, so the cited set understates
    // what the search actually matched.
    //
    // THE `searchRan` TEST IS LOAD-BEARING, and the plain `??` it replaces was
    // a real leak. `sources` is null for two different reasons: no search ran
    // (the manifest paths, which this fallback is FOR), or a search ran and
    // matched nothing above the relevance floor. In the second case `citations`
    // is - with no annotations - the raw unfiltered hit list, so `?? citations`
    // republished to the user the exact set the floor had just rejected, and
    // appended their names to the text the main agent reads as `Sources (n)`.
    // A search that RAN is authoritative, including when it found nothing.
    const sources = response?.searchRan
      ? response.sources ?? null
      : response?.sources ?? response?.citations ?? null;
    // Resolve titles ONCE and use the same array for both consumers: the chips
    // collapse to the title alone, the `Sources (n)` line keeps both names. They
    // must not be resolved separately — two lookups is two chances to disagree
    // about what the user is looking at.
    const labelled = (await withDocumentDisplayNames(this.m_host, sources)) ?? sources;
    if (labelled?.length) presentation?.publishCitations(toChipCitations(labelled));
    if (!response?.text) return "error: knowledge_context_agent_parse_failed";
    return withNamedParts(withSources(response.text, labelled), namedPartLines);
  }

  /** Unity `SearchDocByFileNameAsync` (`:72-99`). Manifest only — never a model call. */
  private async searchDocByFileNameAsync(args: ToolArgs): Promise<string> {
    const prompt = getString(args, "prompt");
    if (!prompt) return "error: prompt_required";

    const trimmed = prompt.trim();
    const presentation = this.m_host.conversationPresentation;
    presentation?.publishToolUsagePrompt(AgentToolNames.SearchDocByFileName, trimmed);
    presentation?.publishToolUsageSource(AgentToolNames.SearchDocByFileName, "string search");

    const response = await this.m_contextEngine.searchDocsByFileNameAsync(trimmed);
    this.m_host.trackHelperAgentUsage?.("Knowledge Context Agent", response?.usage ?? null);
    const sources = response?.sources ?? response?.citations ?? null;
    // One resolve, two consumers - see the note at the sibling call above.
    const labelled = (await withDocumentDisplayNames(this.m_host, sources)) ?? sources;
    if (labelled?.length) presentation?.publishCitations(toChipCitations(labelled));
    if (!response?.text) return "error: knowledge_context_agent_parse_failed";
    return withSources(response.text, labelled);
  }

  /** Unity `FindAndShowPdfAsync` (`:101-165`). */
  private async findAndShowPdfAsync(args: ToolArgs): Promise<string> {
    const prompt = getString(args, "prompt");
    if (!prompt) return "error: prompt_required";

    const presentation = this.m_host.conversationPresentation;
    if (!presentation) return "error: ui_unavailable";

    const trimmed = prompt.trim();
    presentation.publishToolUsagePrompt(AgentToolNames.FindAndShowPDF, trimmed);

    const response = await this.m_contextEngine.findPdfAsync(trimmed);
    this.m_host.trackHelperAgentUsage?.("Knowledge Context Agent", response?.usage ?? null);
    presentation.publishToolUsageSource(
      AgentToolNames.FindAndShowPDF,
      response?.usedStringSearch ? "string search" : "llm",
    );

    if (!response) return "error: pdf_not_found";
    if (response.text?.startsWith("error:")) return response.text.trim();
    if (!response.citations || response.citations.length === 0) {
      return response.text?.trim() || "error: pdf_not_found";
    }
    // Ambiguity is reported back so the agent can ASK, rather than guessing —
    // the slot-by-slot clarification policy in `Campfire_AI_Harness.md`.
    if (response.citations.length !== 1) {
      return response.text?.trim() || "error: multiple_pdf_matches";
    }

    const chosen = response.citations[0];
    const storagePath =
      chosen.storagePath || (chosen.fileName ? `docs/${chosen.fileName}` : null);
    if (!storagePath) return "error: pdf_not_found";

    // ⚠ THE CHIP IS THE AFFORDANCE — this tool SHOWS a reference and never opens
    // a tab itself (user direction 2026-09-07). An agent tool call carries no
    // transient user activation, so the post-fetch `window.open` it used to make
    // is refused by default in Chrome; and because this was the one doc handler
    // that published no citation of its own, that refusal left no tab, no chip
    // and a dead-end sentence. `useSpark.openCitation` awaits nothing before it
    // reserves its tab, so a TAP still carries activation and opens reliably.
    //
    // The chip carries the Define title (`withDocumentDisplayNames`) like both
    // sibling handlers, and `useSpark` de-duplicates on `storagePath` — so a turn
    // that also ran `Search_Doc_By_File_Name` still shows exactly one.
    const labelled = (await withDocumentDisplayNames(this.m_host, [chosen])) ?? [chosen];
    presentation.publishCitations(toChipCitations(labelled));

    // Both names, like the `Sources (n)` line above: the title is what the agent
    // SPEAKS, the file name is what it matches on if the user names a document
    // next (user direction 2026-09-04, extended 2026-09-21).
    const label = agentFacingDocumentName(labelled[0].displayName, chosen.fileName)
      || storagePath;
    return `ok: found ${label}`;
  }
}

/**
 * The searches `Get_Part_Context` built for its part, or null when the agent
 * called this tool itself. Not in the tool's schema, so the model cannot send it.
 */
function readPartSearches(args: ToolArgs): NameSearches | null {
  const value = args[K_PART_SEARCHES_ARG] as NameSearches | undefined;
  return value && Array.isArray(value.searches) && value.searches.length > 0 ? value : null;
}

/** A search that threw or came back with an error. */
function isFailedSearch(response: KnowledgeContextResponse | null): boolean {
  return !response || response.text.trimStart().startsWith("error:");
}

/**
 * Whether a search ran and has something to say: passages, or a real "nothing
 * found". A direct search that failed falls back to `file_search`, whose reply is
 * neither, so its silence under this name means nothing.
 */
function answeredBySearch(response: KnowledgeContextResponse | null): boolean {
  if (isFailedSearch(response)) return false;
  return !!response && (!!response.passages || isNotFoundAnswer(response.text));
}

/**
 * Lists the parts `buildDocsQuery` searched by their other names, in
 * User Context's `"<name>" (aka …)` form. Without it the agent read a passage
 * about a part's hierarchy name as being about a DIFFERENT subject from the
 * alias the user said, and refused an answer it was holding: 7 of 8 searched
 * turns with nothing selected, against 0 of 5 with the line (2026-10-06).
 */
function withNamedParts(text: string, namedPartLines: ReadonlyArray<string>): string {
  if (namedPartLines.length === 0 || text.startsWith("error:")) return text;
  const heading = namedPartLines.length === 1
    ? "Named part (the question uses one of its names; the search used all of them, and a passage may use any of them):"
    : "Named parts (the question uses one of each one's names; the search used all of them, and a passage may use any of them):";
  return `${text}\n\n${heading}\n${namedPartLines.join("\n")}`;
}

/**
 * The direct search's passages as candidate references, each annotated with
 * its document's Define-app title through the same name index the chips use.
 * One lookup for the whole batch; `withDocumentDisplayNames` maps one to one.
 */
async function withPassageDisplayNames(
  host: AgentToolExecutorHost,
  passages: ReadonlyArray<RetrievedPassage>,
): Promise<AICitationCandidate[]> {
  const labelled = await withDocumentDisplayNames(
    host,
    passages.map((passage) => ({
      fileId: passage.fileId,
      fileName: passage.fileName,
      storagePath: passage.storagePath,
    })),
  );
  return passages.map((passage, index) => ({
    fileId: passage.fileId,
    fileName: passage.fileName,
    displayName: labelled?.[index]?.displayName ?? null,
    storagePath: passage.storagePath,
    score: passage.score,
    text: passage.text,
  }));
}

/**
 * The answer convention asks every document answer to say how many sources it
 * found and to offer them all. The sources already reach the panel through
 * `publishCitations`, but the AGENT only ever saw the prose — so it could not
 * say "three sources" or name the runners-up for the user to pick from.
 *
 * Takes the RANKED source list, so the count here and the chips on screen are
 * always the same set. Passing the cited subset told the agent one document
 * existed when the search had matched several.
 *
 * Since Spark Answer References Phase 1 this serves only the open-a-document
 * tools and the `file_search` fallback; the direct search returns passages.
 */
function withSources(
  text: string,
  citations: ReadonlyArray<AIFileCitation> | null,
): string {
  const trimmed = text.trim();
  if (trimmed.startsWith("error:")) return trimmed;

  // Identity is the storage path (falling back to the file id), matching
  // `publishCitations`. Keying on the DISPLAY NAME instead would silently merge
  // two different documents that happen to share a file name, so the count here
  // could disagree with the chips the user is looking at.
  const seen = new Set<string>();
  const names: string[] = [];
  for (const citation of citations ?? []) {
    const key = (citation.storagePath?.trim() || citation.fileId?.trim() || "").toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    // Title AND file name — the title so the agent has a speakable name, the
    // file name so it keeps the specific word it matches a document on.
    const name =
      agentFacingDocumentName(citation.displayName, citation.fileName)
      || citation.storagePath?.trim();
    if (name) names.push(name);
  }
  if (names.length === 0) return trimmed;
  return `${trimmed}\n\nSources (${names.length}): ${names.join(", ")}`;
}
